import { useEffect } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const EXPLORING = [
  "Intergenerational family dynamics", "Cultural and familial expectations", "Marriage and relationship roles",
  "Boundaries within extended families", "Immigration and acculturation", "Identity and belonging",
  "Gender and family roles", "Balancing individual needs with family expectations",
];

const Hero = () => (
  <section className="px-6 md:px-14 pt-14 md:pt-20 pb-12 max-w-4xl">
    <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
      <ArrowLeft size={14} /> Back home
    </motion.a>
    <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={1} className="eyebrow mt-8">Our framework</motion.p>
    <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={2} data-testid="cultural-heading"
      className="font-serif-display mt-4 text-[clamp(2.4rem,5.5vw,4.2rem)] leading-[1.05] font-medium">
      Culturally Responsive <em className="text-[#6e4550]">Psychotherapy.</em>
    </motion.h1>
    <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-5 text-sm font-semibold tracking-[0.18em] uppercase text-[#2c1f20]/70">
      Clinical Psychotherapy for Adults, Couples, Families &amp; Youth
    </motion.p>
    <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed max-w-2xl">
      <p>
        Culture, family systems, and social context can significantly influence how individuals understand
        themselves, relationships, emotional distress, and mental health.
      </p>
      <p>
        Therapy at Haya Healing Psychotherapy considers these factors as part of the broader clinical context rather than
        treating them as separate from psychological wellbeing.
      </p>
    </motion.div>
  </section>
);

const ExploringList = () => (
  <section data-testid="cultural-exploring-list" className="px-6 md:px-14 py-20 md:py-24 border-t border-[#2c1f20]/10 bg-[#f1ece4]/60">
    <Reveal><p className="eyebrow">This may include exploring</p></Reveal>
    <div className="grid sm:grid-cols-2 gap-x-12 gap-y-3.5 mt-8 max-w-4xl">
      {EXPLORING.map((t, i) => (
        <Reveal key={t} i={i % 4} className="flex gap-3 text-[#2c1f20]/80">
          <span className="text-[#8a5a63] mt-0.5">✦</span><span>{t}</span>
        </Reveal>
      ))}
    </div>
  </section>
);

const Logistics = () => (
  <section className="border-y border-[#2c1f20]/10">
    <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#2c1f20]/10">
      {[
        ["English & Urdu", "Move between languages whenever it helps"],
        ["In-person", "Halton Medix Walk-In Clinic — Milton & Burlington"],
        ["Virtual", "Across Ontario, on a secure PHIPA-compliant platform"],
      ].map(([t, d]) => (
        <div key={t} className="py-7 px-6 text-center">
          <p className="font-serif-display text-2xl text-[#6e4550]">{t}</p>
          <p className="text-xs text-[#2c1f20]/55 mt-1">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Cta = () => (
  <footer className="px-6 md:px-14 py-16 md:py-24 bg-[#f1ece4]/60">
    <Reveal><p className="eyebrow">Begin here</p></Reveal>
    <Reveal i={1}>
      <h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">
        Start with a complimentary <em className="text-[#6e4550]">15-minute consultation.</em>
      </h2>
    </Reveal>
    <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
      <a data-testid="cultural-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
      <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
      <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
    </Reveal>
    <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
  </footer>
);

export default function Cultural() {
  useEffect(() => {
    document.title = "Culturally Responsive Therapy in Ontario | Haya Healing Psychotherapy";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Culturally responsive psychotherapy — therapy that treats culture, family systems, and social context as part of the clinical picture. English & Urdu. Milton, Burlington & virtual Ontario.");
  }, []);
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <Hero />
      <ExploringList />
      <Logistics />
      <Cta />
    </div>
  );
}
